import React, { useState } from 'react';
import { FormControl, Box, ButtonGroup, Button, Paper, List, ListItem, ListItemButton, ListItemText, Typography } from '@mui/material';
import { i18n } from '@lingui/core';
import { useLingui } from '@lingui/react';
import { ReactComponent as FranceFlag } from "../images/France.svg";
import { ReactComponent as GreeceFlag } from "../images/Greece.svg";
import { t } from "@lingui/macro";

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
];

const REGIONS = [
  { code: 'gr', name: 'Ελλάδα', flag: { type: 'svg', content: GreeceFlag } },
  { code: 'fr', name: 'France', flag: { type: 'svg', content: FranceFlag } },
];

type LanguageSwitcherType = {
  toggleOut?: () => void;
};


export const LanguageSwitcherWithButtons: React.FC<LanguageSwitcherType> = ({toggleOut}: LanguageSwitcherType) => {
  const { i18n: linguiI18n } = useLingui();
  const [activePanel, setActivePanel] = useState<'regions' | 'languages'>('languages');
  
  const handleLanguageChange = (locale: string) => {
    localStorage.setItem('user-locale', locale);
    toggleOut && toggleOut();
    i18n.activate(locale);
    window.location.reload();
  };
  
  const handlePanelChange = (panel: 'regions' | 'languages') => {
    setActivePanel(panel);
  };
  
  const handleRegionClick = (regionCode: string) => {
    console.log('Selected region:', regionCode);
    // TODO:
    // When ready, implement here the logic for region change
    localStorage.setItem('user-region', regionCode);
    toggleOut && toggleOut();
  };
  
  return (
    <Box sx={{pt: 2, minWidth: 200}}>
      <FormControl fullWidth size="small">
        <ButtonGroup size="small" variant="outlined" aria-label="language switcher" sx={{ border: '1px solid #e0e0e0', mx: 1 }}>
          <Button
            onClick={() => handlePanelChange('regions')}
            sx={{
              padding: '4px 12px',
              minHeight: '32px',
              textTransform: 'none',
              border: "none",
              fontWeight: activePanel === 'regions' ? 'bold' : 'normal',
              backgroundColor: activePanel === 'regions' ? '#c2e2ff' : 'transparent',
              color: activePanel === 'regions' ? '#0062b8' : 'inherit',
              '&:hover': {
                backgroundColor: activePanel === 'languages' ? '#c2e2ff' : undefined,
              },
            }}
          >
            {t`Regions`}
          </Button>
          <Button
            onClick={() => handlePanelChange('languages')}
            sx={{
              padding: '4px 12px',
              minHeight: '32px',
              textTransform: 'none',
              border: "none",
              fontWeight: activePanel === 'languages' ? 'bold' : 'normal',
              backgroundColor: activePanel === 'languages' ? '#c2e2ff' : 'transparent',
              color: activePanel === 'languages' ? '#0062b8' : 'inherit',
              '&:hover': {
                backgroundColor: activePanel === 'languages' ? '#c2e2ff' : undefined,
              },
            }}
          >
            {t`Supported languages`}
          </Button>
        </ButtonGroup>

        <Paper 
          elevation={1} 
          sx={{ 
            mt: 1, 
            maxHeight: 200, 
            overflow: 'auto',
            border: '1px solid #e0e0e0'
          }}
        >
          {activePanel === 'languages' && (
            <List dense>
              {LANGUAGES.map((lang) => (
                <ListItem key={lang.code} disablePadding>
                  <ListItemButton 
                    onClick={() => handleLanguageChange(lang.code)}
                    selected={linguiI18n.locale === lang.code}
                    sx={{
                      backgroundColor: linguiI18n.locale === lang.code ? '#c2e2ff' : 'transparent',
                      '&:hover': {
                        backgroundColor: '#c2e2ff',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#c2e2ff',
                        '&:hover': {
                          backgroundColor: '#c2e2ff',
                        },
                      },
                    }}
                    >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <span style={{ fontSize: '18px' }}>{lang.flag}</span>
                      <ListItemText primary={lang.name} />
                    </Box>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}

          {activePanel === 'regions' && (
            <List dense>
              {REGIONS.map((region) => (
                <ListItem key={region.code} disablePadding>
                  <ListItemButton onClick={() => handleRegionClick(region.code)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Box
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 24, 
                          height: 24,
                          borderRadius: "50%",
                          overflow: "hidden",
                        }}
                        >
                        <region.flag.content 
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'fill',
                          objectPosition: 'center', 
                        }} 
                        />
                      </Box>
                      <ListItemText primary={region.name} />
                    </Box>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      </FormControl>
    </Box>
  );
};

const LanguageSwitcher:React.FC<LanguageSwitcherType> = ({toggleOut}: LanguageSwitcherType) => {
  const { i18n: linguiI18n } = useLingui();

  const handleLanguageChange = (locale: string) => {
    localStorage.setItem('user-locale', locale);
    toggleOut && toggleOut();
    i18n.activate(locale);
    window.location.reload();
  };

  return (
    <Box sx={{minWidth: 200}}>
      <FormControl fullWidth size="small">
        <Paper 
          elevation={1} 
          sx={{ 
            pt: 2,
            maxHeight: 200, 
            overflow: 'auto',
            border: '1px solid #e0e0e0'
          }}
        >

          <Typography variant="subtitle1" sx={{ px: 2, fontWeight: 'bold' }}>
            {t`Supported languages`}
          </Typography>
            <List dense>
              {LANGUAGES.map((lang) => (
                <ListItem key={lang.code} disablePadding>
                  <ListItemButton 
                    onClick={() => handleLanguageChange(lang.code)}
                    selected={linguiI18n.locale === lang.code}
                    sx={{
                      backgroundColor: linguiI18n.locale === lang.code ? '#c2e2ff' : 'transparent',
                      '&:hover': {
                        backgroundColor: '#c2e2ff',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#c2e2ff',
                        '&:hover': {
                          backgroundColor: '#c2e2ff',
                        },
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <ListItemText primary={lang.name} />
                    </Box>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
        </Paper>
      </FormControl>
    </Box>
  );
};

export default LanguageSwitcher;